/** Google Maps tile renderer. Pixels are renderer resources, not weather state. */
import { buildNWSWMSTileUrl } from "./radarProvider";
import { recolorTileAsync } from "./recolorRadarTile";
import { RadarTileCache } from "./RadarTileCache";

type TileLoadingCallback = (loading: boolean) => void;
export class RecoloredNWSMapType implements google.maps.MapType {
  tileSize: google.maps.Size;
  maxZoom = 10;
  minZoom = 4;
  alt: string | null = null;
  projection: google.maps.Projection | null = null;
  radius = 6378137;
  private tiles = new Map<HTMLCanvasElement, AbortController>();
  private currentOpacity = 0;
  private inFlightTiles = 0;
  private onTileLoadingChange: TileLoadingCallback | null = null;
  private disposed = false;

  constructor(
    private isoTimestamp: string,
    public name: string,
    private cache: RadarTileCache,
  ) {
    this.tileSize = new google.maps.Size(256, 256);
  }
  setOnTileLoadingChange(callback: TileLoadingCallback): void {
    this.onTileLoadingChange = callback;
  }
  private loading(delta: number): void {
    const previous = this.inFlightTiles;
    this.inFlightTiles = Math.max(0, previous + delta);
    if ((previous === 0) !== (this.inFlightTiles === 0))
      this.onTileLoadingChange?.(this.inFlightTiles > 0);
  }
  getTile(
    coord: google.maps.Point,
    zoom: number,
    ownerDocument: Document,
  ): HTMLCanvasElement {
    const canvas = ownerDocument.createElement("canvas");
    canvas.width = canvas.height = 256;
    canvas.style.opacity = "0";
    if (this.disposed) {
      canvas.width = canvas.height = 0;
      return canvas;
    }
    const controller = new AbortController();
    this.tiles.set(canvas, controller);
    if (zoom < this.minZoom) return canvas;
    const key = `${this.isoTimestamp}/${zoom}/${coord.x}/${coord.y}`;
    const cached = this.cache.get(key);
    if (cached) {
      canvas.getContext("2d")?.putImageData(cached, 0, 0);
      canvas.style.opacity = String(this.currentOpacity);
      return canvas;
    }
    this.loading(1);
    const image = new Image();
    image.crossOrigin = "anonymous";
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      image.onload = image.onerror = null;
      controller.signal.removeEventListener("abort", cancel);
      this.loading(-1);
    };
    const cancel = () => {
      finish();
      image.src = "";
    };
    controller.signal.addEventListener("abort", cancel, { once: true });
    image.onload = async () => {
      try {
        const recolored = await recolorTileAsync(
          image,
          ownerDocument,
          controller.signal,
        );
        if (!controller.signal.aborted) {
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(recolored, 0, 0);
            this.cache.set(key, ctx.getImageData(0, 0, 256, 256));
          }
          canvas.style.opacity = String(this.currentOpacity);
        }
        recolored.width = recolored.height = 0;
      } catch {
        if (!controller.signal.aborted) {
          canvas.getContext("2d")?.drawImage(image, 0, 0);
          canvas.style.opacity = String(this.currentOpacity);
        }
      } finally {
        finish();
      }
    };
    image.onerror = finish;
    image.src = buildNWSWMSTileUrl(coord, zoom, this.isoTimestamp);
    return canvas;
  }
  releaseTile(tile: Element): void {
    const canvas = tile as HTMLCanvasElement;
    this.tiles.get(canvas)?.abort();
    this.tiles.delete(canvas);
    canvas.width = canvas.height = 0;
  }

  /** Cancel tiles for a frame that is no longer visible, while retaining its decoded cache. */
  releaseAllTiles(): void {
    for (const canvas of this.tiles.keys()) this.releaseTile(canvas);
  }

  dispose(): void {
    this.disposed = true;
    this.releaseAllTiles();
    this.onTileLoadingChange = null;
  }
  getMemoryUsage() {
    return {
      ...this.cache.usage(`${this.isoTimestamp}/`),
      canvases: this.tiles.size,
      canvasBytes: Array.from(this.tiles.keys()).reduce(
        (sum, tile) => sum + tile.width * tile.height * 4,
        0,
      ),
      pending: this.inFlightTiles,
    };
  }
  setOpacity(opacity: number): void {
    this.currentOpacity = opacity;
    for (const canvas of this.tiles.keys())
      canvas.style.opacity = String(opacity);
  }
}
